import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiSave,
  FiDownload,
  FiRotateCcw,
  FiRotateCw,
  FiZoomIn,
  FiZoomOut,
  FiCheck,
  FiLoader,
} from 'react-icons/fi';
import * as fabric from 'fabric';
import { jsPDF } from 'jspdf';
import useEditorStore from '../../stores/useEditorStore';
import { CANVAS_SIZE_PRESETS, CANVAS_CUSTOM_PROPS } from '../../constants/editorConstants';
import { saveProject } from '../../services/firestoreService';
import { uploadCanvasImages } from '../../services/storageService';
import { exportPptFromData, downloadBlob } from '../../services/editorService';
import { fabricToParserFormat, convertSlide } from '../../services/parserConverter';
import { inferGroupType, stripLayoutManagers } from '../../utils/groupUtils';

const EditorHeader = () => {
  const navigate = useNavigate();
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isSizeMenuOpen, setIsSizeMenuOpen] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [exportStatus, setExportStatus] = useState(null); // null | 'exporting'
  const [isSnapMenuOpen, setIsSnapMenuOpen] = useState(false);
  const [isGuideMenuOpen, setIsGuideMenuOpen] = useState(false);
  const [isMarginMenuOpen, setIsMarginMenuOpen] = useState(false);
  const sizeMenuRef = useRef(null);
  const exportMenuRef = useRef(null);
  const snapMenuRef = useRef(null);
  const guideMenuRef = useRef(null);
  const marginMenuRef = useRef(null);

  const {
    canvas,
    zoom,
    setZoom,
    undo,
    redo,
    history,
    historyIndex,
    canvasSize,
    setCanvasSize,
    projectId,
    setProjectId,
    projectName,
    setProjectName,
    backgroundColor,
    slidesData,
    currentSlideIndex,
    snapThreshold,
    setSnapThreshold,
    guideThickness,
    setGuideThickness,
    overflowMargin,
    setOverflowMargin,
  } = useEditorStore();

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sizeMenuRef.current && !sizeMenuRef.current.contains(e.target)) {
        setIsSizeMenuOpen(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setIsExportMenuOpen(false);
      }
      if (snapMenuRef.current && !snapMenuRef.current.contains(e.target)) {
        setIsSnapMenuOpen(false);
      }
      if (guideMenuRef.current && !guideMenuRef.current.contains(e.target)) {
        setIsGuideMenuOpen(false);
      }
      if (marginMenuRef.current && !marginMenuRef.current.contains(e.target)) {
        setIsMarginMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 캔버스 크기가 변경되면 input 값 동기화
  useEffect(() => {
    setCustomWidth(canvasSize.width.toString());
    setCustomHeight(canvasSize.height.toString());
  }, [canvasSize]);

  // 내보내기 핸들러
  const handleExport = (format) => {
    if (!canvas) return;

    let dataURL;
    let filename;

    switch (format) {
      case 'png':
        dataURL = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });
        filename = `${projectName}.png`;
        break;
      case 'jpg':
        dataURL = canvas.toDataURL({ format: 'jpeg', quality: 0.9, multiplier: 2 });
        filename = `${projectName}.jpg`;
        break;
      case 'json':
        const json = JSON.stringify(canvas.toObject(CANVAS_CUSTOM_PROPS), null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        dataURL = URL.createObjectURL(blob);
        filename = `${projectName}.json`;
        break;
      default:
        return;
    }

    const link = document.createElement('a');
    link.href = dataURL;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (format === 'json') {
      URL.revokeObjectURL(dataURL);
    }

    setIsExportMenuOpen(false);
  };

  // 모든 슬라이드의 canvasState를 수집 (미방문 슬라이드는 layers → canvasState 변환)
  const collectAllSlideStates = async () => {
    useEditorStore.getState().saveCurrentSlide();
    const latestSlidesData = useEditorStore.getState().slidesData;

    if (latestSlidesData.length === 0) {
      const canvasJSON = canvas.toObject(CANVAS_CUSTOM_PROPS);
      canvasJSON.backgroundColor = canvas.backgroundColor || backgroundColor;
      return [JSON.stringify(canvasJSON)];
    }

    const results = [];
    for (let idx = 0; idx < latestSlidesData.length; idx++) {
      const slide = latestSlidesData[idx];

      if (idx === currentSlideIndex) {
        const canvasJSON = canvas.toObject(CANVAS_CUSTOM_PROPS);
        canvasJSON.backgroundColor = canvas.backgroundColor || backgroundColor;
        results.push(JSON.stringify(canvasJSON));
      } else if (slide.canvasState) {
        // 이미 방문한 슬라이드: 저장된 canvasState 사용
        results.push(slide.canvasState);
      } else if (slide.layers && slide.layers.length > 0) {
        // 미방문 슬라이드: layers를 임시 캔버스로 변환하여 canvasState 생성
        const canvasEl = document.createElement('canvas');
        canvasEl.width = canvasSize.width;
        canvasEl.height = canvasSize.height;
        const tempCanvas = new fabric.StaticCanvas(canvasEl, {
          width: canvasSize.width,
          height: canvasSize.height,
        });
        tempCanvas.backgroundColor = '#ffffff';

        const fabricObjects = await convertSlide(slide.layers);
        for (const obj of fabricObjects) {
          tempCanvas.add(obj);
        }
        tempCanvas.renderAll();

        const tempJSON = tempCanvas.toObject(CANVAS_CUSTOM_PROPS);
        tempJSON.backgroundColor = tempCanvas.backgroundColor || '#ffffff';
        results.push(JSON.stringify(tempJSON));

        tempCanvas.dispose();
      }
    }

    return results;
  };

  // fabric 객체를 base64 이미지로 래스터화
  const rasterizeObject = async (obj) => {
    const center = obj.getCenterPoint();
    const w = (obj.width || 100) * (obj.scaleX || 1);
    const h = (obj.height || 100) * (obj.scaleY || 1);

    try {
      const cloned = await obj.clone();
      const padding = (obj.strokeWidth || 0) * 2;
      const cw = Math.ceil(w + padding);
      const ch = Math.ceil(h + padding);

      const canvasEl = document.createElement('canvas');
      canvasEl.width = cw;
      canvasEl.height = ch;
      const tempCanvas = new fabric.StaticCanvas(canvasEl, { width: cw, height: ch });

      cloned.set({
        left: cw / 2,
        top: ch / 2,
        originX: 'center',
        originY: 'center',
        angle: 0,
      });
      tempCanvas.add(cloned);
      tempCanvas.renderAll();

      const dataURL = tempCanvas.toDataURL({ format: 'png' });
      const base64 = dataURL.split(',')[1];
      tempCanvas.dispose();

      return {
        type: 'image',
        offset: [center.x - w / 2, center.y - h / 2],
        size: [w, h],
        opacity: Math.round((obj.opacity ?? 1) * 100),
        rotation: obj.angle || 0,
        base64,
      };
    } catch (e) {
      console.error('래스터화 오류:', e);
      return null;
    }
  };

  // canvasState JSON에서 슬라이드를 이미지로 렌더링
  const renderSlideToImage = async (canvasStateStr) => {
    return new Promise((resolve) => {
      const canvasEl = document.createElement('canvas');
      canvasEl.width = canvasSize.width;
      canvasEl.height = canvasSize.height;
      const tempCanvas = new fabric.StaticCanvas(canvasEl, {
        width: canvasSize.width,
        height: canvasSize.height,
      });

      const state = stripLayoutManagers(JSON.parse(canvasStateStr));
      tempCanvas.loadFromJSON(state).then(() => {
        tempCanvas.renderAll();
        const dataURL = tempCanvas.toDataURL({ format: 'png', multiplier: 2 });
        tempCanvas.dispose();
        resolve(dataURL);
      });
    });
  };

  // canvasState에서 파서 형식 레이어 추출

  const convertSlideForPpt = async (canvasStateStr) => {
    return new Promise((resolve) => {
      const canvasEl = document.createElement('canvas');
      canvasEl.width = canvasSize.width;
      canvasEl.height = canvasSize.height;
      const tempCanvas = new fabric.StaticCanvas(canvasEl, {
        width: canvasSize.width,
        height: canvasSize.height,
      });

      const state = stripLayoutManagers(JSON.parse(canvasStateStr));

      tempCanvas.loadFromJSON(state).then(async () => {
        tempCanvas.renderAll();
        const objects = tempCanvas.getObjects();
        const stateObjects = state.objects || [];

        // 1단계: JSON에서 커스텀 속성 복원 시도
        for (let i = 0; i < objects.length && i < stateObjects.length; i++) {
          const jsonObj = stateObjects[i];
          const obj = objects[i];
          if (jsonObj.id) obj.id = jsonObj.id;
          if (jsonObj.name) obj.name = jsonObj.name;

          if (jsonObj._isTextBoxGroup) {
            obj._isTextBoxGroup = true;
            obj._bgColor = jsonObj._bgColor;
            obj._bgAlpha = jsonObj._bgAlpha;
            obj._bgShape = jsonObj._bgShape;
            obj._borderColor = jsonObj._borderColor || null;
            obj._borderOpacity = jsonObj._borderOpacity ?? 100;
            obj._borderWidth = jsonObj._borderWidth || 0;
          }
          if (jsonObj._isTableGroup) {
            obj._isTableGroup = true;
            obj._tableRows = jsonObj._tableRows;
            obj._tableCols = jsonObj._tableCols;
            obj._tableColWidths = jsonObj._tableColWidths;
            obj._tableRowHeights = jsonObj._tableRowHeights;
            const childObjs = obj.getObjects ? obj.getObjects() : [];
            const jsonChilds = jsonObj.objects || [];
            for (let j = 0; j < childObjs.length && j < jsonChilds.length; j++) {
              if (jsonChilds[j]._isTableCell) childObjs[j]._isTableCell = true;
              if (jsonChilds[j]._isTableCellText) childObjs[j]._isTableCellText = true;
              if (jsonChilds[j]._cellRow !== undefined) childObjs[j]._cellRow = jsonChilds[j]._cellRow;
              if (jsonChilds[j]._cellCol !== undefined) childObjs[j]._cellCol = jsonChilds[j]._cellCol;
              if (jsonChilds[j]._gridSpan !== undefined) childObjs[j]._gridSpan = jsonChilds[j]._gridSpan;
              if (jsonChilds[j]._rowSpan !== undefined) childObjs[j]._rowSpan = jsonChilds[j]._rowSpan;
              if (jsonChilds[j]._marL !== undefined) childObjs[j]._marL = jsonChilds[j]._marL;
              if (jsonChilds[j]._marR !== undefined) childObjs[j]._marR = jsonChilds[j]._marR;
              if (jsonChilds[j]._marT !== undefined) childObjs[j]._marT = jsonChilds[j]._marT;
              if (jsonChilds[j]._marB !== undefined) childObjs[j]._marB = jsonChilds[j]._marB;
            }
          }
        }

        // 2단계: 플래그가 없는 그룹은 자식 구조로 타입 추론
        for (const obj of objects) {
          if (obj.type === 'group' && !obj._isTextBoxGroup && !obj._isTableGroup) {
            inferGroupType(obj);
          }
        }

        const layers = [];

        for (const obj of objects) {
          const type = obj.type;
          if (type === 'textbox' || type === 'i-text' || type === 'text') {
            layers.push(fabricToParserFormat(obj));
          } else if (type === 'group' && obj._isTextBoxGroup) {
            layers.push(fabricToParserFormat(obj));
          } else if (type === 'group' && obj._isTableGroup) {
            layers.push(fabricToParserFormat(obj));
          } else if (type === 'group') {
            // 일반 그룹: flat하게 펼쳐서 각 자식을 개별 처리
            const groupCenter = obj.getCenterPoint();
            const gsx = obj.scaleX || 1;
            const gsy = obj.scaleY || 1;
            const children = obj.getObjects ? obj.getObjects() : [];

            for (const child of children) {
              const absLeft = groupCenter.x + child.left * gsx;
              const absTop = groupCenter.y + child.top * gsy;
              const absSx = (child.scaleX || 1) * gsx;
              const absSy = (child.scaleY || 1) * gsy;
              const childType = child.type;

              if (childType === 'textbox' || childType === 'i-text' || childType === 'text') {
                // 텍스트: 절대 좌표 클론 후 fabricToParserFormat
                const cloned = await child.clone();
                cloned.set({ left: absLeft, top: absTop, scaleX: absSx, scaleY: absSy, originX: 'center', originY: 'center' });
                // 커스텀 속성 복사 (clone()은 Fabric 기본 속성만 복사)
                CANVAS_CUSTOM_PROPS.forEach(prop => {
                  if (child[prop] !== undefined) cloned[prop] = child[prop];
                });
                cloned._gradientConfig = child._gradientConfig;
                layers.push(fabricToParserFormat(cloned));
              } else {
                // 도형 등: 절대 좌표 클론 후 개별 래스터화
                const cloned = await child.clone();
                cloned.set({ left: absLeft, top: absTop, scaleX: absSx, scaleY: absSy, originX: 'center', originY: 'center' });
                const rasterized = await rasterizeObject(cloned);
                if (rasterized) layers.push(rasterized);
              }
            }
          } else {
            const rasterized = await rasterizeObject(obj);
            if (rasterized) layers.push(rasterized);
          }
        }

        tempCanvas.dispose();
        resolve(layers);
      });
    });
  };

  // PPT 내보내기
  const handleExportPpt = async () => {
    if (!canvas) return;
    setExportStatus('exporting');
    setIsExportMenuOpen(false);

    try {
      // 1. 자동 저장
      await handleSave();

      // 2. 슬라이드 데이터 수집
      const slideStates = await collectAllSlideStates();

      // 3. 각 슬라이드를 parser format으로 변환
      const dataList = [];
      for (const stateStr of slideStates) {
        const layers = await convertSlideForPpt(stateStr);
        dataList.push(layers);
      }

      // 4. 백엔드 호출
      const blob = await exportPptFromData({
        canvas_size: [canvasSize.width, canvasSize.height],
        data_list: dataList,
      });

      // 5. 다운로드
      downloadBlob(blob, `${projectName}.pptx`);
    } catch (err) {
      console.error('PPT 내보내기 오류:', err);
      alert('PPT 내보내기에 실패했습니다: ' + err.message);
    } finally {
      setExportStatus(null);
    }
  };

  // PDF 내보내기
  const handleExportPdf = async () => {
    if (!canvas) return;
    setExportStatus('exporting');
    setIsExportMenuOpen(false);

    try {
      // 1. 슬라이드 데이터 수집
      const slideStates = await collectAllSlideStates();

      // 2. 페이지 크기 설정 (캔버스 크기 기준, px → mm 변환)
      const PX_TO_MM = 25.4 / 96;
      const pageWidth = canvasSize.width * PX_TO_MM;
      const pageHeight = canvasSize.height * PX_TO_MM;

      const pdf = new jsPDF({
        orientation: canvasSize.width > canvasSize.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight],
      });

      // 3. 각 슬라이드를 이미지로 렌더링 → PDF 페이지 추가
      for (let i = 0; i < slideStates.length; i++) {
        if (i > 0) pdf.addPage([pageWidth, pageHeight]);

        const dataURL = await renderSlideToImage(slideStates[i]);
        pdf.addImage(dataURL, 'PNG', 0, 0, pageWidth, pageHeight);
      }

      // 4. 다운로드
      pdf.save(`${projectName}.pdf`);
    } catch (err) {
      console.error('PDF 내보내기 오류:', err);
      alert('PDF 내보내기에 실패했습니다: ' + err.message);
    } finally {
      setExportStatus(null);
    }
  };

  // Firestore에 프로젝트 저장
  const handleSave = async () => {
    if (!canvas) return;

    setSaveStatus('saving');

    try {
      // 현재 슬라이드 상태 저장
      useEditorStore.getState().saveCurrentSlide();

      // 최신 slidesData 가져오기
      const latestSlidesData = useEditorStore.getState().slidesData;

      // slidesData가 비어있으면 현재 캔버스만 저장
      let slidesToSave;
      if (latestSlidesData.length === 0) {
        const canvasJSON = canvas.toObject(CANVAS_CUSTOM_PROPS);
        canvasJSON.backgroundColor = canvas.backgroundColor || backgroundColor;
        slidesToSave = [{ canvasState: JSON.stringify(canvasJSON) }];
      } else {
        slidesToSave = latestSlidesData.map((slide, idx) => {
          if (idx === currentSlideIndex) {
            const canvasJSON = canvas.toObject(CANVAS_CUSTOM_PROPS);
            canvasJSON.backgroundColor = canvas.backgroundColor || backgroundColor;
            return { canvasState: JSON.stringify(canvasJSON) };
          }
          return { canvasState: slide.canvasState || null };
        });
      }

      const isNewProject = !projectId;
      const saveId = projectId || `proj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      // base64 이미지를 Storage에 업로드 후 URL로 교체
      for (let i = 0; i < slidesToSave.length; i++) {
        if (slidesToSave[i].canvasState) {
          slidesToSave[i].canvasState = await uploadCanvasImages(
            slidesToSave[i].canvasState, saveId, i
          );
        }
      }

      const result = await saveProject({
        projectId: isNewProject ? null : saveId,
        forcedId: isNewProject ? saveId : undefined,
        name: projectName,
        canvasSize,
        backgroundColor: (canvas.backgroundColor && canvas.backgroundColor !== 'transparent')
          ? canvas.backgroundColor : (backgroundColor || '#ffffff'),
        userId: null,
        slidesData: slidesToSave,
      });

      if (result.success) {
        if (!projectId) {
          setProjectId(result.projectId);
        }
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('저장 오류:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  // 줌 핸들러
  const handleZoomIn = () => setZoom(Math.min(zoom + 0.05, 2));
  const handleZoomOut = () => setZoom(Math.max(zoom - 0.05, 0.1));

  // 캔버스 크기 변경 핸들러
  const handleCanvasSizeChange = (preset) => {
    if (!canvas) return;
    setCanvasSize({ width: preset.width, height: preset.height });
    setCustomWidth(preset.width.toString());
    setCustomHeight(preset.height.toString());
    setIsSizeMenuOpen(false);
  };

  // 커스텀 크기 적용 핸들러
  const handleApplyCustomSize = () => {
    if (!canvas) return;

    const width = parseInt(customWidth, 10);
    const height = parseInt(customHeight, 10);

    if (isNaN(width) || isNaN(height) || width < 1 || height < 1) {
      alert('올바른 크기를 입력해주세요.');
      return;
    }

    const finalWidth = Math.min(width, 10000);
    const finalHeight = Math.min(height, 10000);

    setCanvasSize({ width: finalWidth, height: finalHeight });
    setCustomWidth(finalWidth.toString());
    setCustomHeight(finalHeight.toString());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApplyCustomSize();
  };

  const getCurrentSizeLabel = () => {
    const preset = CANVAS_SIZE_PRESETS.find(
      p => p.width === canvasSize.width && p.height === canvasSize.height
    );
    return preset ? preset.label : `${canvasSize.width}×${canvasSize.height}`;
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // 저장 버튼 아이콘
  const renderSaveIcon = () => {
    if (saveStatus === 'saving') return <FiLoader size={18} style={{ animation: 'spin 1s linear infinite' }} />;
    if (saveStatus === 'saved') return <FiCheck size={18} color="#22c55e" />;
    return <FiSave size={18} />;
  };

  const renderSaveLabel = () => {
    if (saveStatus === 'saving') return '저장 중...';
    if (saveStatus === 'saved') return '저장됨';
    if (saveStatus === 'error') return '오류';
    return '저장';
  };

  return (
    <div
      style={{
        height: '56px',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: '16px',
      }}
    >
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* 뒤로가기 */}
      <button onClick={() => navigate('/main')} style={iconButtonStyle} title="뒤로가기">
        <FiArrowLeft size={20} />
      </button>

      <div style={dividerStyle} />

      {/* 프로젝트 이름 */}
      <input
        type="text"
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        style={{
          border: 'none',
          fontSize: '16px',
          fontWeight: '600',
          color: '#374151',
          outline: 'none',
          padding: '4px 8px',
          borderRadius: '4px',
          width: '200px',
        }}
        placeholder="프로젝트 이름"
      />

      <div style={dividerStyle} />

      {/* 실행 취소 / 다시 실행 */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={undo}
          disabled={!canUndo}
          style={{ ...iconButtonStyle, opacity: canUndo ? 1 : 0.4, cursor: canUndo ? 'pointer' : 'not-allowed' }}
          title="실행 취소 (Ctrl+Z)"
        >
          <FiRotateCcw size={18} />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          style={{ ...iconButtonStyle, opacity: canRedo ? 1 : 0.4, cursor: canRedo ? 'pointer' : 'not-allowed' }}
          title="다시 실행 (Ctrl+Y)"
        >
          <FiRotateCw size={18} />
        </button>
      </div>

      <div style={dividerStyle} />

      {/* 캔버스 크기 */}
      <div ref={sizeMenuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setIsSizeMenuOpen(!isSizeMenuOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: '6px',
            backgroundColor: '#ffffff', color: '#374151', fontSize: '13px', cursor: 'pointer',
          }}
          title="캔버스 크기"
        >
          <span>{getCurrentSizeLabel()}</span>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>▼</span>
        </button>

        {isSizeMenuOpen && (
          <div style={dropdownStyle}>
            {CANVAS_SIZE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleCanvasSizeChange(preset)}
                style={{
                  ...menuItemStyle,
                  backgroundColor: canvasSize.width === preset.width && canvasSize.height === preset.height ? '#eff6ff' : 'transparent',
                  fontWeight: canvasSize.width === preset.width && canvasSize.height === preset.height ? '600' : '400',
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 커스텀 크기 입력 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <input type="number" value={customWidth} onChange={(e) => setCustomWidth(e.target.value)} onKeyDown={handleKeyDown} placeholder="가로" style={sizeInputStyle} />
        <span style={{ color: '#9ca3af', fontSize: '12px' }}>×</span>
        <input type="number" value={customHeight} onChange={(e) => setCustomHeight(e.target.value)} onKeyDown={handleKeyDown} placeholder="세로" style={sizeInputStyle} />
        <button
          onClick={handleApplyCustomSize}
          style={{ padding: '4px 8px', border: '1px solid #3b82f6', borderRadius: '4px', backgroundColor: '#3b82f6', color: '#ffffff', fontSize: '12px', cursor: 'pointer' }}
        >
          적용
        </button>
      </div>

      {/* 중앙 공간 */}
      <div style={{ flex: 1 }} />

      {/* 스냅 설정 */}
      <div ref={snapMenuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setIsSnapMenuOpen(!isSnapMenuOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: '6px',
            backgroundColor: '#ffffff', color: '#374151', fontSize: '12px', cursor: 'pointer',
          }}
          title="스냅 임계값"
        >
          <span style={{ color: '#9ca3af' }}>스냅</span>
          <span style={{ fontWeight: '600' }}>{snapThreshold}px</span>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>▼</span>
        </button>

        {isSnapMenuOpen && (
          <div style={{ ...dropdownStyle, right: 0, left: 'auto', minWidth: '80px' }}>
            {[5, 10, 15, 20, 50, 100].map((val) => (
              <button
                key={val}
                onClick={() => { setSnapThreshold(val); setIsSnapMenuOpen(false); }}
                style={{
                  ...menuItemStyle,
                  backgroundColor: snapThreshold === val ? '#eff6ff' : 'transparent',
                  fontWeight: snapThreshold === val ? '600' : '400',
                }}
              >
                {val}px
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 가이드 두께 설정 */}
      <div ref={guideMenuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setIsGuideMenuOpen(!isGuideMenuOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: '6px',
            backgroundColor: '#ffffff', color: '#374151', fontSize: '12px', cursor: 'pointer',
          }}
          title="가이드라인 두께"
        >
          <span style={{ color: '#9ca3af' }}>가이드</span>
          <span style={{ fontWeight: '600' }}>{guideThickness}px</span>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>▼</span>
        </button>

        {isGuideMenuOpen && (
          <div style={{ ...dropdownStyle, right: 0, left: 'auto', minWidth: '90px' }}>
            {[
              { value: 1.5, label: '1.5px' },
              { value: 3, label: '3px' },
              { value: 6, label: '6px' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setGuideThickness(opt.value); setIsGuideMenuOpen(false); }}
                style={{
                  ...menuItemStyle,
                  backgroundColor: guideThickness === opt.value ? '#eff6ff' : 'transparent',
                  fontWeight: guideThickness === opt.value ? '600' : '400',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 오버플로우 마진 설정 */}
      <div ref={marginMenuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setIsMarginMenuOpen(!isMarginMenuOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: '6px',
            backgroundColor: '#ffffff', color: '#374151', fontSize: '12px', cursor: 'pointer',
          }}
          title="오버플로우 마진 (캔버스 밖 오브젝트 여백)"
        >
          <span style={{ color: '#9ca3af' }}>마진</span>
          <span style={{ fontWeight: '600' }}>{overflowMargin}px</span>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>▼</span>
        </button>

        {isMarginMenuOpen && (
          <div style={{ ...dropdownStyle, right: 0, left: 'auto', minWidth: '80px' }}>
            {[50, 100, 200, 500].map((val) => (
              <button
                key={val}
                onClick={() => { setOverflowMargin(val); setIsMarginMenuOpen(false); }}
                style={{
                  ...menuItemStyle,
                  backgroundColor: overflowMargin === val ? '#eff6ff' : 'transparent',
                  fontWeight: overflowMargin === val ? '600' : '400',
                }}
              >
                {val}px
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* 줌 컨트롤 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button onClick={handleZoomOut} style={iconButtonStyle} title="축소"><FiZoomOut size={18} /></button>
        <span style={{ fontSize: '13px', color: '#6b7280', minWidth: '50px', textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </span>
        <button onClick={handleZoomIn} style={iconButtonStyle} title="확대"><FiZoomIn size={18} /></button>
      </div>

      <div style={dividerStyle} />

      {/* 저장 */}
      <button
        onClick={handleSave}
        disabled={saveStatus === 'saving'}
        style={{
          ...buttonStyle,
          backgroundColor: saveStatus === 'saved' ? '#f0fdf4' : saveStatus === 'error' ? '#fef2f2' : '#ffffff',
          borderColor: saveStatus === 'saved' ? '#22c55e' : saveStatus === 'error' ? '#ef4444' : '#e5e7eb',
          color: saveStatus === 'saved' ? '#22c55e' : saveStatus === 'error' ? '#ef4444' : '#374151',
          cursor: saveStatus === 'saving' ? 'not-allowed' : 'pointer',
        }}
        title="저장 (Ctrl+S)"
      >
        {renderSaveIcon()}
        <span>{renderSaveLabel()}</span>
      </button>

      {/* 내보내기 */}
      <div ref={exportMenuRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
          disabled={exportStatus === 'exporting'}
          style={{
            ...buttonStyle,
            backgroundColor: exportStatus === 'exporting' ? '#93c5fd' : '#3b82f6',
            color: '#ffffff',
            cursor: exportStatus === 'exporting' ? 'not-allowed' : 'pointer',
          }}
        >
          {exportStatus === 'exporting' ? (
            <FiLoader size={18} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <FiDownload size={18} />
          )}
          <span>{exportStatus === 'exporting' ? '내보내는 중...' : '내보내기'}</span>
        </button>

        {isExportMenuOpen && !exportStatus && (
          <div style={{ ...dropdownStyle, right: 0, left: 'auto' }}>
            <button onClick={() => handleExport('png')} style={menuItemStyle}>PNG</button>
            <button onClick={() => handleExport('jpg')} style={menuItemStyle}>JPG</button>
            <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '4px 0' }} />
            <button onClick={handleExportPpt} style={menuItemStyle}>PPT</button>
            <button onClick={handleExportPdf} style={menuItemStyle}>PDF</button>
          </div>
        )}
      </div>
    </div>
  );
};

const iconButtonStyle = {
  width: '36px', height: '36px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', borderRadius: '6px', backgroundColor: 'transparent',
  color: '#374151', cursor: 'pointer', transition: 'background-color 0.15s',
};

const buttonStyle = {
  display: 'flex', alignItems: 'center', gap: '6px',
  padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '6px',
  backgroundColor: '#ffffff', color: '#374151', fontSize: '14px',
  fontWeight: '500', cursor: 'pointer', transition: 'all 0.15s',
};

const dividerStyle = { width: '1px', height: '24px', backgroundColor: '#e5e7eb' };

const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, marginTop: '4px',
  backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', overflow: 'hidden',
  zIndex: 100, minWidth: '120px',
};

const menuItemStyle = {
  display: 'block', width: '100%', padding: '10px 16px', border: 'none',
  backgroundColor: 'transparent', textAlign: 'center', fontSize: '14px',
  color: '#374151', cursor: 'pointer',
};

const sizeInputStyle = {
  width: '70px', padding: '4px 8px', border: '1px solid #e5e7eb',
  borderRadius: '4px', fontSize: '13px', textAlign: 'center', outline: 'none',
};

export default EditorHeader;
